import { useCallback, useEffect, useRef, useState } from 'react';
import { BenchmarkRequestError, RunReferenceStatus, benchmarkService } from '../../../../services/benchmarkService';
import { followContextStream } from '../contextStream';
import { humanise } from '../../BenchmarkVocabulary';

/** How often a running submission is checked. A full run takes minutes. */
export const REFERENCE_POLL_MS = 5000;
/**
 * How many times an unknown reference is asked about again before giving up:
 * a submission made a moment ago may not have its flow log written yet.
 */
export const NOT_FOUND_RETRIES = 6;

export interface RunReferenceState {
    status: RunReferenceStatus | null;
    /** The most recent step the run reported, while it runs. */
    latestStep: string | null;
    error: string | null;
    retry: () => void;
}

/** A readable name for the step a context-stream event came from. */
function stepName(event: unknown): string | null {
    if (!event || typeof event !== 'object') return null;
    const e = event as Record<string, any>;
    const code = e.stage ?? e.activity_code ?? e.activityCode ?? e.activity_template_code;
    return code ? humanise(String(code).replace(/_\d{8}T\d{6}$/, '')) : null;
}

/**
 * Follows a submission from its referenceId until it is scored or known not to
 * be: polls /runs/by-reference, and while the run is going follows its context
 * stream for a sense of progress. The stream is a nicety — without it (no
 * context graph on the environment) polling still settles the outcome.
 *
 * Pass null to follow nothing.
 */
export function useRunReference(referenceId: string | null): RunReferenceState {
    const [status, setStatus] = useState<RunReferenceStatus | null>(null);
    const [latestStep, setLatestStep] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);
    const pollNowRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        if (!referenceId) return undefined;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const stream = new AbortController();
        let following = false;
        let notFoundRetries = 0;

        setStatus(null);
        setLatestStep(null);
        setError(null);

        const follow = () => {
            if (following) return;
            following = true;
            followContextStream(referenceId, {
                signal: stream.signal,
                onEvent: (event) => {
                    const step = stepName(event);
                    if (step && !cancelled) setLatestStep(step);
                },
                // The run has finished; ask now rather than at the next tick.
                onDone: () => { if (!cancelled) pollNowRef.current(); }
            }).catch(() => undefined);
        };

        const poll = async () => {
            if (timer) clearTimeout(timer);
            timer = null;
            try {
                const next = await benchmarkService.getRunByReference(referenceId);
                if (cancelled) return;
                setStatus(next);
                setError(null);
                if (next.status === 'running') {
                    follow();
                    timer = setTimeout(poll, REFERENCE_POLL_MS);
                } else {
                    stream.abort();
                }
            } catch (err) {
                if (cancelled) return;
                // Just submitted: the flow log may not be written yet.
                if (err instanceof BenchmarkRequestError && err.status === 404) {
                    if (notFoundRetries < NOT_FOUND_RETRIES) {
                        notFoundRetries += 1;
                        timer = setTimeout(poll, REFERENCE_POLL_MS);
                        return;
                    }
                    setError('No submission was found for this reference.');
                } else {
                    setError(err instanceof BenchmarkRequestError ? err.message : 'Could not check on this submission.');
                }
                stream.abort();
            }
        };
        pollNowRef.current = () => { poll(); };

        poll();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            stream.abort();
        };
    }, [referenceId, attempt]);

    const retry = useCallback(() => setAttempt((n) => n + 1), []);
    return { status, latestStep, error, retry };
}
